import { Component, effect, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { AppRoutes, FundService } from '@gs-2.0/core';
import { FundDisplay, Step } from '@gs-2.0/shared';
import { TransactionService } from '../../../_core/api/transaction.service';
import PageTitles from '../../../_core/constants/page-titles';
import Urls from '../../../_core/constants/urls';
import SharedMethodsHelpers from '../../../_core/helpers/shared-methods.helpers';
import { GrantTransferData } from '../../../_core/models/grantData';
import { Fund } from '../../../_core/models/fund';
import { GlobalConfigApiService } from '../../../_core/api/global-config.service';
import { TitleService } from '../../../_core/services/title.service';

@Component({
  selector: 'app-transfer',
  templateUrl: './transfer.component.html',
  styleUrls: ['./transfer.component.scss'],
  standalone: false,
})
export class TransferComponent implements OnInit {
  urls = Urls;
  currentFund: Fund;
  transferFormData: any;
  transferFormValid = false;

  transferDisplayData = new GrantTransferData();

  stepperSteps: Step[] = [
    { label: 'contribute.step.details', number: 1 },
    { label: 'contribute.step.summary', number: 2 },
    { label: 'transfer.step.confirmation', mobileLabel: 'transfer.step.confirm', number: 3 },
  ];

  currentStep = 1;
  showError = false;
  loading = false;
  pageLoading = true;

  // Dependencies — use inject() so signals can reference them in field initializers
  private router = inject(Router);
  private titleService = inject(TitleService);
  private fundService = inject(FundService);
  private transactionService = inject(TransactionService);
  private globalConfigApiService = inject(GlobalConfigApiService);

  // Signal references — same pattern as dashboard
  funds = this.fundService.funds;
  selectedFundId = this.fundService.selectedFundId;
  selectedCurrency = this.fundService.selectedCurrency;

  numberFormatConfig = { decimalSymbol: '.', digitGroupingSymbol: ',', digitGrouping: '123,456,789' };

  private initialized = false;

  get activeStepIndex(): number {
    return this.currentStep - 1;
  }

  constructor() {
    // Watch for fund changes — if user switches to a non-eligible fund, redirect to dashboard
    effect(() => {
      const isEligible = this.fundService.isTransferEligible();
      const selectedFund = this.fundService.selectedFund();
      // Only redirect after page is initialized and funds are loaded
      if (this.initialized && selectedFund && !isEligible) {
        this.router.navigate([AppRoutes.FUND_DASHBOARD]);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.loadNumberFormat();
    await this.fundService.loadFunds();
    this.currentFund = this.fundService.selectedFund() as unknown as Fund;
    this.pageLoading = false;
    this.initialized = true;
    this.setPageTitle();
  }

  private loadNumberFormat(): void {
    this.globalConfigApiService.getConfiguration().subscribe({
      next: (config: any) => {
        if (config?.response?.numberFormat) {
          this.numberFormatConfig = {
            decimalSymbol: config.response.numberFormat.decimalSymbol?.value || '.',
            digitGroupingSymbol: config.response.numberFormat.digitGroupingSymbol?.value || ',',
            digitGrouping: config.response.numberFormat.digitGrouping || '123,456,789',
          };
        }
      },
    });
  }

  setPageTitle(): void {
    this.titleService.titleChanged(PageTitles.TRANSFER);
  }

  computeDisplayData(): void {
    this.transferDisplayData = { ...this.transferFormData };
    this.transferDisplayData.timing =
      this.transferFormData.timing === 'specificDate'
        ? this.transferFormData.date
        : 'As soon as possible';
  }

  goToSummary(): void {
    if (this.transferFormValid) {
      window.scrollTo(0, 0);
      this.computeDisplayData();
      this.currentStep = 2;
    }
  }

  goToForm(): void {
    window.scrollTo(0, 0);
    this.currentStep = 1;
    this.showError = false;
  }

  onSubmit(): void {
    this.loading = true;
    const payload = this.buildTransferPayload(this.transferFormData, this.currentFund);
    this.transactionService.submitTransactionFundToFund(payload).subscribe({
      next: () => {
        // Transfer moved funds — refresh so the dashboard's next visit shows
        // the new balances on both source and destination funds (F2 would
        // otherwise serve the pre-transfer cache).
        this.fundService.loadFunds({ force: true });
        this.currentStep = 3;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.showError = true;
      },
    });
  }

  onFundChanged(fund: FundDisplay): void {
    this.fundService.setSelectedFund(fund.id);
    this.router.navigate([AppRoutes.FUND_DASHBOARD]);
    // If the new fund is transfer-eligible, update the page; otherwise effect() redirects
    // if (this.fundService.isTransferEligible()) {
    //   this.currentFund = this.fundService.selectedFund() as unknown as Fund;
    //   this.currentStep = 1;
    //   this.transferFormData = null;
    //   this.transferFormValid = false;
    //   this.transferDisplayData = new GrantTransferData();
    //   this.showError = false;
    //   this.setPageTitle();
    // }
  }

  goToPending(): void {
    this.router.navigate(['fund/activity/pending']);
  }

  goToDashboard(): void {
    this.router.navigate([AppRoutes.FUND_DASHBOARD]);
  }

  reloadTransfer(): void {
    const currentUrl = this.router.url;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate([currentUrl]);
  }

  private buildTransferPayload(formData: any, fund: Fund): any {
    return {
      isFromGrantsTab: false,
      senderCseg5: fund.cseg5,
      senderFundName: fund.fundName,
      receiverCseg5: formData.receiverCseg5,
      receiverFundName: formData.receiverFundName,
      currency: this.fundService.selectedCurrency()?.id || fund.currency,
      amount: formData.amount,
      date: formData.date ? SharedMethodsHelpers.dateToMilliseconds(formData.date, true) : '',
      privacy: false,
      charityInstruction: '',
      additionalNotes: formData.additionalNotes || '',
      charityProject: false,
      fundType: fund.fundType,
      isRecurring: false,
      recurrencePattern: null,
      startDate: null,
      endDate: null,
      timeZone: this.getTimezone(),
    };
  }

  private getTimezone(): string {
    const offset = -new Date().getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offset) / 60).toString().padStart(2, '0');
    const mins = (Math.abs(offset) % 60).toString().padStart(2, '0');
    return `UTC${sign}${hours}:${mins}`;
  }
}
